import { apiClient } from '../api'

// Shared cache of Bearer-authed asset images. The assets endpoint needs the
// auth token, so a plain `<img src>` gets a 401 — we fetch the bytes through the
// authed client and render them from an object URL. Caching by `src` lets us:
//   1. warm the blob on the `images` SSE event, before the answer tokens arrive,
//      so the image is ready the moment its `[IMG#]` marker resolves; and
//   2. survive the AssetImage remounts caused by re-parsing the streaming
//      markdown every token — a warm cache hit is instant, so the spinner never
//      reappears.
// Object URLs live until `clearAssetImageCache()` (called on sign-out) so blobs
// don't leak across sessions.
const blobUrlCache = new Map<string, string>() // src -> object URL
const inflight = new Map<string, Promise<string>>() // src -> pending fetch

// Fetch an asset (or reuse the cached/in-flight fetch) and resolve to its object
// URL. Safe to call repeatedly for the same `src` — the fetch is deduped.
export function loadAssetImage(src: string): Promise<string> {
  const cached = blobUrlCache.get(src)
  if (cached) return Promise.resolve(cached)

  let pending = inflight.get(src)
  if (!pending) {
    pending = apiClient.document
      .fetchAssetBlob(src)
      .then(blob => {
        const url = URL.createObjectURL(blob)
        blobUrlCache.set(src, url)
        return url
      })
      .finally(() => inflight.delete(src))
    inflight.set(src, pending)
  }
  return pending
}

// Synchronous cache peek, so a remounting component can render the image with no
// spinner when the blob is already warm.
export function getCachedAssetImage(src: string): string | null {
  return blobUrlCache.get(src) ?? null
}

// Revoke every object URL and drop the cache. Call on sign-out so a new session
// doesn't reuse the previous user's blobs.
export function clearAssetImageCache(): void {
  Array.from(blobUrlCache.values()).forEach(url => URL.revokeObjectURL(url))
  blobUrlCache.clear()
  inflight.clear()
}
